import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LineChartSerie, LineChartSerieData, PollResultAverage, PollResultValue } from './poll-result.type';
import { GraphUtils } from 'app/frontend/lib/analytics/graphs/utils.service';
import { format } from 'date-fns-tz';
import { C } from '@angular/cdk/keycodes';
import { isNull } from 'lodash';
import { ITeam } from "@teammood/team";

@Injectable({
    providedIn: 'root'
  })
  export class PollResultService {

    constructor(
        private translate: TranslateService,
    ) {}

    formatClosedPollResult(data: PollResultValue[]){
        const sortedData = data.sort((a, b) => b.position - a.position)
        return sortedData.map(item => {
            return {
                name: item.label,
                y: item.count,
                color: item.choice === 'don-t-want-to-answer' ? 'lightgrey' : highchartsDefaultColors[item.position]
            }
        });
    }

    getTeamName(teamId: string, teams: ITeam[]){
        if (teamId) {
            const team = teams.find(team => team.id === teamId);
            return team.name;
        }
    }

    formatPollTrendResultSeries(series, teams: ITeam[]): LineChartSerie[] {
        return series.map(serie => {
            return {
                type: 'spline',
                name: this.getTeamName(serie.teamId, teams),
                data: serie.values,
                lineWidth: 4,
                marker: {
                    enabled: true
                },
            }
        });
    }

    formatPollTrendResultDates(dates: Date[]): string[] {
        return dates.map(date => {
            return format(date, 'PP');
        });
    }

    formatAgreeDisagreePollResult(data: PollResultValue[]){
        const sortedData = data.sort((a, b) => b.position - a.position)
        return sortedData.map(item => {
            return {
                name: item.label,
                data: [item.count],
                color: colors[item.choice]
            }
        });
    }

    formatScalePollResult(data: PollResultValue[]){
        const sortedData = data.sort((a, b) => b.position - a.position)
        return sortedData.map(item => {
            return {
                name: item.label,
                data: [item.count],
                color: colors[item.choice]
            }
        });
    }

    formatScalePollResultForPyramidChart(data: PollResultValue[]){
        const sortedData = data.sort((a, b) => b.position - a.position)
        let totalCount = 0;
        sortedData.forEach((current) => {
            if(current.choice !== 'don-t-want-to-answer'){
                totalCount += current.count;
            }
        });
        const transformedData = sortedData.map(item => {
            switch(item.position){
                case 1: return {
                    index: 2,
                    name: item.label,
                    customLabel: '1/5 - '+item.count+' vote(s) (' + (item.count/totalCount*100).toFixed(1) + '%)',
                    data: [item.count * -1],
                    color: colors['1'],
                }
                case 2: return {
                    index: 3,
                    name: item.label,
                    customLabel: '2/5 - '+item.count+' vote(s) (' + (item.count/totalCount*100).toFixed(1) + '%)',
                    data: [item.count * -1],
                    color: colors['2'],
                }
                case 3: return [{
                    name: item.label,
                    customLabel: '3/5 - '+item.count+' vote(s) (' + (item.count/totalCount*100).toFixed(1) + '%)',
                    data: [item.count/2 * -1],
                    color: colors['3'],
                    id: 'neutral',
                    index: 4
                }, {
                    name: item.label,
                    customLabel: '3/5 - '+item.count+' vote(s) (' + (item.count/totalCount*100).toFixed(1) + '%)',
                    data: [item.count/2],
                    color: colors['3'],
                    linkedTo: 'neutral',
                    index: 2
                }]
                // case 3: return {
                //     index: 2,
                //     name: item.label,
                //     customLabel: '3/5 - '+item.count+' vote(s)',
                //     data: [item.count],
                //     color: colors['3'],
                // }
                case 4: return {
                    index: 1,
                    name: item.label,
                    customLabel: '4/5 - '+item.count+' vote(s) (' + (item.count/totalCount*100).toFixed(1) + '%)',
                    data: [item.count],
                    color: colors['4'],
                }
                case 5: return {
                    index: 0,
                    name: item.label,
                    customLabel: '5/5 - '+item.count+' vote(s) (' + (item.count/totalCount*100).toFixed(1) + '%)',
                    data: [item.count],
                    color: colors['5'],
                }
                default: break;
            }
            
        })
        const flatData = transformedData.flat(1);
        flatData.pop() // last element is undefined because of "dont want to answer" item (to fix?)
        return flatData;
    }

    formatFiveStarsPollResult(data: PollResultValue[]){
        const sortedData = data.sort((a, b) => b.position - a.position);
        let points = 0;
        sortedData.forEach(current => {
            if(current.choice !== "don-t-want-to-answer"){
                points += current.count*Number(current.choice);
            }
        }, 0);
        const number = sortedData.reduce((total, current) => total + current.count, 0);
        return {
            averageScore: (points/number).toFixed(2),
            people: number
        };
    }

    getParticipationCount(data: PollResultValue[]): number{
        return data.reduce((total, current) => total + current.count, 0);
    }

    formatPollResultGifs(textContent: string){
        let transformedContent = textContent;
        const urlRegex = /(<img([\w\W]+?)>)/g;
        const urls = textContent.match(urlRegex);
        if(urls !== null && urls.length > 0){
            const patterns = this.transformUrlsInPatterns(urls);
            patterns.forEach((pattern, index) => {
                transformedContent = transformedContent.replace(urls[index], pattern);
            })
        }
        return transformedContent;
    }

    private transformUrlsInPatterns(urls: string[]){
        return urls.map(url => {
            if(url.includes('.gif')){
                const text = this.translate.instant('user_activity.link_pattern.gif');
                const cleanUrl = url.split('<img src="')[1].split('">')[0];
                return '<a href="'+ cleanUrl +'" target="_blank">'+ text +'</a>';
            }
        })
    }

    formatPollTrendResult(data: PollResultAverage[]){
        if (data) {
            const transformedData = data.map(item => {
                return {
                    y: item.value,
                    note: item.value === null ? 'N/A' : item.value.toFixed(2) + ' /5',
                    datetime: this.translate.instant('poll_result.period') + ' ' + format(item.date, 'PPP')
                }
            })
            return transformedData;
        } else return {};
    }
    
    formatClosedPollAverageTrendResult(teams: LineChartSerie[]): LineChartSerie {
        const averages: { [key: string]: { name: string; data: number[]; count: number } } = {};
        teams.forEach(team => {
            team.data.forEach(choice => {
                if (!averages[choice.name]) {
                    averages[choice.name] = {
                        name: choice.name,
                        data: Array(choice.data.length).fill(0),
                        count: 0
                    };
                }
                choice.data.forEach((value, index) => {
                    averages[choice.name].data[index] += value;
                });
                averages[choice.name].count++;
            });
        });
        // Calculer les moyennes
        const averagedData: LineChartSerieData[] = Object.values(averages).map(choice => ({
            name: choice.name,
            data: choice.data.map(value => (value / choice.count) || 0), // Éviter la division par zéro
        }));
    
        return {
            type: "spline",
            name: this.translate.instant('trend_all_team_average_graph_legend'),
            data: averagedData,
            lineWidth: 4,
            marker: {
                enabled: true
            }
        };
    }

    formatClosedPollTrendResult(valuesByTeams, teams = []): LineChartSerie[] {
        const series = valuesByTeams.map(valuesByTeam => {
            const teamName = this.getTeamName(valuesByTeam.teamId, teams);
            const teamData = this.closedPollDataToLineChartData(valuesByTeam.values);
            return {
                type: 'spline',
                name: teamName,
                data: teamData,
                lineWidth: 4,
                marker: {
                    enabled: true
                },
            }
        });
        return series;
    }

    closedPollDataToLineChartData(valuesByTeamArrayByOccurence): LineChartSerie[] {
        if(valuesByTeamArrayByOccurence){
            // Création d'un objet pour stocker les choix et leurs pourcentages associés
            const choicesMap = {};
            // Parcourir chaque tableau de données
            valuesByTeamArrayByOccurence.forEach((group, index) => {
                group.forEach(item => {
                    if (!choicesMap[item.choice]) {
                        // Initialiser l'objet avec le choix et un tableau de pourcentages
                        choicesMap[item.choice] = {
                            name: item.choice,
                            data: []
                        };
                    }
                    // Ajouter le pourcentage pour ce groupe
                    choicesMap[item.choice].data[index] = item.percentage;
                });
            });
            // Convertir l'objet en tableau
            return Object.values(choicesMap);
        }
    }

    formatPollTrendCompiledResult(results){
        const initalArray = results.values[0].values.map(item => {
                return {
                    choice: item.choice,
                    countDetail: [],
                    label: item.label,
                    position: item.position
                }
            }
        )

        results.values.forEach(item => {
            item.values.forEach(element => {
                initalArray.find(x => x.choice === element.choice).countDetail.push(element.count)
            })
            
        })

        const sortedData = initalArray.sort((a, b) => b.position - a.position)
        const transformedData = sortedData.map((item) => {
            return {
                name: item.label,
                data: item.countDetail,
                color: item.choice === 'don-t-want-to-answer' ? 'lightgrey' : highchartsDefaultColors[item.position]
            }
        });
        return transformedData;
    }
}

const highchartsDefaultColors = [ "#2caffe", "#544fc5", "#00cf68", "#fe6a35", "#6b8abc", "#d568fb", "#2ee0ca", "#fa4b42", "#feb56a", "#91e8e1" ];
const stronglyAgree = '#43AA8B';
const agree =  '#90BE6D';
const neutral = '#F9C74F';
const disagree = '#F3722C';
const stronglyDisagree = '#F94144';
const colors = {
    "don-t-want-to-answer": 'lightgrey',
    '5': stronglyAgree,      
    '4': agree,    
    '3': neutral, 
    '2': disagree,
    '1': stronglyDisagree 
}


  




